import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface MoveDocumentsBetweenPropertiesRequest {
  document_ids: string[];
  source_property_location_id: number;
  target_property_location_id: number;
}

export interface MoveDocumentsBetweenPropertiesResponse {
  success: boolean;
  message: string;
  moved_documents_count: number;
  updated_calendar_events_count: number;
}

const METHOD = 'POST';
const ENDPOINT = 'document/move-between-properties';
export const QUERY_KEY = () => getQueryKey(METHOD, ENDPOINT, {});

export const useMoveDocumentsBetweenProperties = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MoveDocumentsBetweenPropertiesRequest) => {
      try {
        const data = await handleApiCall<
          MoveDocumentsBetweenPropertiesResponse,
          MoveDocumentsBetweenPropertiesRequest
        >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), request);

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to move documents between properties.', { variant: 'error' });

        // eslint-disable-next-line no-console
        console.error(err);

        throw err;
      }
    },
    onSuccess: () => {
      // Invalidate all folder and document queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['vault'] });
    },
  });
};
